import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Autocomplete from '@mui/material/Autocomplete';
import {
    useListOrganizations,
    useCreateOrganization,
    useUpdateOrganization,
    useDeleteOrganization,
    useGetOrganizationUsers,
    useOrgAssignUser,
    useOrgRemoveUser,
    useSearchOrganizationUsers,
} from '../services/appService';

const formatDate = (dateInput) => {
    if (!dateInput) return 'N/A';
    try {
        const date = new Date(dateInput);
        if (isNaN(date.getTime())) return 'Invalid Date';
        return date.toLocaleDateString('de-DE', { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch (e) {
        return 'Invalid Date';
    }
};

function OrganizationsPage() {
    const { appId } = useParams();
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Create dialog
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newOrgName, setNewOrgName] = useState('');
    const [newOrgAboutMe, setNewOrgAboutMe] = useState('');
    const [newOrgIsVerein, setNewOrgIsVerein] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);

    // Detail/edit dialog
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [editName, setEditName] = useState('');
    const [editAboutMe, setEditAboutMe] = useState('');
    const [editIsVerein, setEditIsVerein] = useState(false);
    const [orgUsers, setOrgUsers] = useState([]);
    const [orgUsersLoading, setOrgUsersLoading] = useState(false);
    const [editLoading, setEditLoading] = useState(false);

    // Delete confirmation
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [orgToDelete, setOrgToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // User search/assign
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);

    const listOrganizationsHook = useListOrganizations();
    const createOrganizationHook = useCreateOrganization();
    const updateOrganizationHook = useUpdateOrganization();
    const deleteOrganizationHook = useDeleteOrganization();
    const getOrgUsersHook = useGetOrganizationUsers();
    const assignUserHook = useOrgAssignUser();
    const removeUserHook = useOrgRemoveUser();
    const searchUsersHook = useSearchOrganizationUsers();

    const fetchOrganizations = useCallback(async () => {
        if (!appId) return;
        setLoading(true);
        setError('');
        try {
            const data = await listOrganizationsHook(appId);
            setOrganizations(data || []);
        } catch (err) {
            console.error("Error fetching organizations:", err);
            setError(err.message || 'Failed to load organizations.');
        } finally {
            setLoading(false);
        }
    }, [appId, listOrganizationsHook]);

    useEffect(() => {
        fetchOrganizations();
    }, [fetchOrganizations]);

    // Create organization
    const handleCreate = async () => {
        if (!newOrgName.trim()) return;
        setCreateLoading(true);
        try {
            await createOrganizationHook(appId, { name: newOrgName, about_me: newOrgAboutMe, is_verein: newOrgIsVerein });
            setCreateDialogOpen(false);
            setNewOrgName('');
            setNewOrgAboutMe('');
            setNewOrgIsVerein(false);
            fetchOrganizations();
        } catch (err) {
            setError(err.message || 'Failed to create organization.');
        } finally {
            setCreateLoading(false);
        }
    };

    // Open detail dialog
    const handleOpenDetail = async (org) => {
        setSelectedOrg(org);
        setEditName(org.name);
        setEditAboutMe(org.about_me || '');
        setEditIsVerein(org.is_verein || false);
        setDetailDialogOpen(true);
        setOrgUsersLoading(true);
        try {
            const users = await getOrgUsersHook(appId, org.id);
            setOrgUsers(users || []);
        } catch (err) {
            console.error("Error fetching org users:", err);
            setOrgUsers([]);
        } finally {
            setOrgUsersLoading(false);
        }
    };

    // Update organization
    const handleUpdate = async () => {
        if (!selectedOrg) return;
        setEditLoading(true);
        try {
            await updateOrganizationHook(appId, selectedOrg.id, {
                name: editName,
                about_me: editAboutMe,
                is_verein: editIsVerein,
            });
            setDetailDialogOpen(false);
            fetchOrganizations();
        } catch (err) {
            setError(err.message || 'Failed to update organization.');
        } finally {
            setEditLoading(false);
        }
    };

    // Delete organization
    const handleDeleteConfirm = async () => {
        if (!orgToDelete) return;
        setDeleteLoading(true);
        try {
            await deleteOrganizationHook(appId, orgToDelete.id);
            setDeleteDialogOpen(false);
            setOrgToDelete(null);
            if (detailDialogOpen && selectedOrg?.id === orgToDelete.id) {
                setDetailDialogOpen(false);
            }
            fetchOrganizations();
        } catch (err) {
            setError(err.message || 'Failed to delete organization.');
        } finally {
            setDeleteLoading(false);
        }
    };

    // Search users
    const handleSearchUsers = async (query) => {
        setSearchQuery(query);
        if (!query || query.length < 2) {
            setSearchResults([]);
            return;
        }
        setSearchLoading(true);
        try {
            const results = await searchUsersHook(appId, query);
            setSearchResults(results || []);
        } catch (err) {
            console.error("Error searching users:", err);
            setSearchResults([]);
        } finally {
            setSearchLoading(false);
        }
    };

    // Assign user to org
    const handleAssignUser = async (user) => {
        if (!selectedOrg || !user) return;
        try {
            await assignUserHook(appId, selectedOrg.id, { user_id: user.id });
            // Refresh org users
            const users = await getOrgUsersHook(appId, selectedOrg.id);
            setOrgUsers(users || []);
            setSearchQuery('');
            setSearchResults([]);
            fetchOrganizations(); // refresh member count
        } catch (err) {
            setError(err.message || 'Failed to assign user.');
        }
    };

    // Remove user from org
    const handleRemoveUser = async (userId) => {
        if (!selectedOrg) return;
        try {
            await removeUserHook(appId, selectedOrg.id, userId);
            const users = await getOrgUsersHook(appId, selectedOrg.id);
            setOrgUsers(users || []);
            fetchOrganizations(); // refresh member count
        } catch (err) {
            setError(err.message || 'Failed to remove user.');
        }
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
                    Organisationen
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateDialogOpen(true)}
                >
                    Organisation erstellen
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Name</strong></TableCell>
                                <TableCell><strong>Verein</strong></TableCell>
                                <TableCell><strong>Mitglieder</strong></TableCell>
                                <TableCell><strong>Einladungscode</strong></TableCell>
                                <TableCell><strong>Erstellt am</strong></TableCell>
                                <TableCell align="right"><strong>Aktionen</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {organizations.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                            Keine Organisationen vorhanden.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                organizations.map((org) => (
                                    <TableRow
                                        key={org.id}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => handleOpenDetail(org)}
                                    >
                                        <TableCell>{org.name}</TableCell>
                                        <TableCell>
                                            {org.is_verein ? (
                                                <Chip label="Verein" color="primary" size="small" />
                                            ) : (
                                                <Chip label="Nein" size="small" variant="outlined" />
                                            )}
                                        </TableCell>
                                        <TableCell>{org.member_count || 0}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                                {org.invite_code || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{formatDate(org.created_at)}</TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => { e.stopPropagation(); handleOpenDetail(org); }}
                                                title="Bearbeiten"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={(e) => { e.stopPropagation(); setOrgToDelete(org); setDeleteDialogOpen(true); }}
                                                title="Löschen"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Create Organization Dialog */}
            <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Neue Organisation erstellen</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Name"
                        fullWidth
                        value={newOrgName}
                        onChange={(e) => setNewOrgName(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Beschreibung"
                        fullWidth
                        multiline
                        rows={3}
                        value={newOrgAboutMe}
                        onChange={(e) => setNewOrgAboutMe(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={newOrgIsVerein}
                                onChange={(e) => setNewOrgIsVerein(e.target.checked)}
                            />
                        }
                        label="Ist ein Verein"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)}>Abbrechen</Button>
                    <Button onClick={handleCreate} variant="contained" disabled={createLoading || !newOrgName.trim()}>
                        {createLoading ? <CircularProgress size={24} /> : 'Erstellen'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Detail / Edit Dialog */}
            <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Organisation bearbeiten
                </DialogTitle>
                <DialogContent>
                    <TextField
                        margin="dense"
                        label="Name"
                        fullWidth
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        sx={{ mb: 2, mt: 1 }}
                    />
                    <TextField
                        margin="dense"
                        label="Beschreibung"
                        fullWidth
                        multiline
                        rows={3}
                        value={editAboutMe}
                        onChange={(e) => setEditAboutMe(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={editIsVerein}
                                onChange={(e) => setEditIsVerein(e.target.checked)}
                            />
                        }
                        label="Ist ein Verein"
                    />

                    {selectedOrg && (
                        <Box sx={{ mt: 2, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                ID: {selectedOrg.id} | Einladungscode: {selectedOrg.invite_code || '-'}
                            </Typography>
                        </Box>
                    )}

                    <Divider sx={{ my: 3 }} />

                    <Typography variant="h6" gutterBottom>
                        Mitglieder ({orgUsers.length})
                    </Typography>

                    {/* User search/assign */}
                    <Box sx={{ mb: 2 }}>
                        <Autocomplete
                            freeSolo
                            options={searchResults}
                            getOptionLabel={(option) => 
                                typeof option === 'string' ? option : `${option.display_name || 'Unnamed'} (${option.email || option.id})`
                            }
                            inputValue={searchQuery}
                            onInputChange={(e, newValue) => handleSearchUsers(newValue)}
                            onChange={(e, value) => {
                                if (value && typeof value !== 'string') {
                                    handleAssignUser(value);
                                }
                            }}
                            loading={searchLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Benutzer suchen und hinzufügen..."
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                <PersonAddIcon sx={{ color: 'action.active', mr: 1 }} />
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Avatar src={option.avatar_url} sx={{ width: 28, height: 28 }}>
                                            {(option.display_name || '?')[0]}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2">{option.display_name || 'Unnamed'}</Typography>
                                            <Typography variant="caption" color="text.secondary">{option.email || option.id}</Typography>
                                        </Box>
                                    </Box>
                                </li>
                            )}
                        />
                    </Box>

                    {orgUsersLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : orgUsers.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            Keine Mitglieder in dieser Organisation.
                        </Typography>
                    ) : (
                        <List dense>
                            {orgUsers.map((user, index) => (
                                <React.Fragment key={user.id}>
                                    <ListItem
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                size="small"
                                                color="error"
                                                onClick={() => handleRemoveUser(user.id)}
                                                title="Entfernen"
                                            >
                                                <PersonRemoveIcon />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemAvatar>
                                            <Avatar src={user.avatar_url} sx={{ width: 32, height: 32 }}>
                                                {(user.display_name || '?')[0]}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {user.display_name || 'Unnamed'}
                                                    <Chip
                                                        label={user.role}
                                                        size="small"
                                                        color={user.role === 'admin' ? 'primary' : 'default'}
                                                        variant="outlined"
                                                    />
                                                    {user.is_verified && (
                                                        <Chip label="Verifiziert" size="small" color="success" variant="outlined" />
                                                    )}
                                                </Box>
                                            }
                                            secondary={`Beigetreten: ${formatDate(user.joined_at)} | ID: ${user.id}`}
                                        />
                                    </ListItem>
                                    {index < orgUsers.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={() => { setOrgToDelete(selectedOrg); setDeleteDialogOpen(true); }}
                    >
                        Löschen
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <Button onClick={() => setDetailDialogOpen(false)}>Schließen</Button>
                    <Button onClick={handleUpdate} variant="contained" disabled={editLoading}>
                        {editLoading ? <CircularProgress size={24} /> : 'Speichern'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Organisation löschen?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Soll die Organisation <strong>{orgToDelete?.name}</strong> wirklich gelöscht werden?
                        Alle Mitgliedschaften werden ebenfalls entfernt.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setDeleteDialogOpen(false); setOrgToDelete(null); }}>Abbrechen</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleteLoading}>
                        {deleteLoading ? <CircularProgress size={24} /> : 'Löschen'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default OrganizationsPage;
